/**
 * Prefill "idea" cards shown beneath the landing-screen composer. Clicking a card
 * drops its `prompt` into the input (it does not auto-send) so a first-time user
 * sees their words land in the box and can edit before sending.
 *
 * Prompts deliberately model good canvas-agent first messages.
 */

import {
  FileText,
  Lightbulb,
  Newspaper,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

export interface CanvasComposerSuggestion {
  id: string;
  label: string;
  /** One-line hint shown under the label. */
  hint: string;
  /** Text dropped into the composer when the card is clicked. */
  prompt: string;
  icon: LucideIcon;
}

export const CANVAS_COMPOSER_SUGGESTIONS: CanvasComposerSuggestion[] = [
  {
    id: "brainstorm",
    label: "Brainstorm ideas",
    hint: "Spin up a wall of possibilities",
    prompt:
      "Build me an idea board for what I could make on this canvas: six ideas, each as its own chromeless HTML block styled with the design system, laid out in a three by two grid. Each block is one idea card with a title, two lines on what it is and why it is worth doing, and a small tag naming the Cohesive capability it uses (documents, styled pages, generated images, flow diagrams, web research, plans). Make the six ideas concrete projects a first-time user could start today.",
    icon: Lightbulb,
  },
  {
    id: "outline",
    label: "Draft an outline",
    hint: "Structure a project or doc",
    prompt:
      "Draft a project outline for a fictional initiative: launching a YouTube channel about restoring vintage bicycles. The outline is a document: goal, audience, a four-week plan to the first three videos, roles, and open questions, about a page. Around it, add three chromeless HTML blocks styled with the design system: a polished horizontal timeline of the four weeks with a milestone card per week and the three video releases marked; a roles card with a row per person and what they own; and an open-questions card with each question as a line and a checkbox. Arrange the document on the left and the three blocks in a column on the right, timeline on top.",
    icon: FileText,
  },
  {
    id: "headlines",
    label: "Summarize today's headlines",
    hint: "Today's news, in vintage newspaper print",
    prompt:
      "Search the web for today's top headlines across world news, technology, business and science, from reputable sources. Then lay out the front page of The Cohesive Times across the canvas as separate chromeless HTML blocks styled with the design system's serif type like a vintage broadsheet: a masthead block with the paper's name, today's date and an edition line, spanning the top; a lead story block with a headline, a one-line deck and a short summary; four to six story cards, one block each, in two columns under the lead, each with a headline, a one-line summary and its source as a small footer line; and one pull-quote block. Add a document titled with today's date that holds the full summaries and source links for every story. Arrange it as a page: masthead across the top, lead under it, the story columns below, the document to the right.",
    icon: Newspaper,
  },
  {
    id: "capabilities",
    label: "Show me what's possible",
    hint: "See what this canvas can do",
    prompt:
      "Give me a quick tour of what this canvas can do, laid out as a walk from left to right. Start with a chromeless HTML block styled with the design system as the tour card: a title, three sentences on what the canvas is, and a numbered legend of what follows. Then, in order: a note; a short document; one generated image; a small flow diagram made of shapes and connectors showing how a brief becomes a deck; a polished HTML metric card with a headline number and a sparkline; and a polished HTML checklist block with three things to try next. Put a label above each example naming what it is and one way I could use it. Seven or eight items in all.",
    icon: Sparkles,
  },
];
